import { gql } from "apollo-boost";
import noLogo from "../../assets/img/brand/noLogo";

export const COMPANY_INFO_CACHE_KEY = "dhn.companyInfo";
export const COMPANY_INFO_UPDATED_EVENT = "dhn.companyInfo.updated";

export const GET_COMPANY_INFO = gql`
  query CompanyInfo {
    legalEntity {
      id
      businessName
      commercialName
      rnc
      address
      electronicTaxReceipt
      logoBase64
      logoPath
      logoName
      logoMimetype
    }
  }
`;

const getSessionStorage = () => {
  try {
    if (typeof window === "undefined") return null;
    return window.sessionStorage || null;
  } catch (_) {
    return null;
  }
};

export const getCachedCompanyInfo = () => {
  try {
    const storage = getSessionStorage();
    if (!storage) return null;
    const rawValue = storage.getItem(COMPANY_INFO_CACHE_KEY);
    return rawValue ? JSON.parse(rawValue) : null;
  } catch (_) {
    return null;
  }
};

export const notifyCompanyInfoUpdated = (companyInfo) => {
  if (typeof window === "undefined" || !window.dispatchEvent) return;
  window.dispatchEvent(
    new CustomEvent(COMPANY_INFO_UPDATED_EVENT, { detail: companyInfo || null }),
  );
};

export const setCachedCompanyInfo = (companyInfo) => {
  try {
    const storage = getSessionStorage();
    if (storage) {
      if (companyInfo) {
        storage.setItem(COMPANY_INFO_CACHE_KEY, JSON.stringify(companyInfo));
      } else {
        storage.removeItem(COMPANY_INFO_CACHE_KEY);
      }
    }
  } catch (_) {}

  notifyCompanyInfoUpdated(companyInfo);
};

export const getCompanyLogoSource = (companyInfo, fallback = "") =>
  companyInfo?.logoBase64 || companyInfo?.logoPath || fallback;

export const fetchCompanyInfo = async (client, fetchPolicy = "network-only") => {
  if (!client?.query) return null;

  const { data } = await client.query({
    query: GET_COMPANY_INFO,
    fetchPolicy,
  });

  return data?.legalEntity || null;
};

export const getCompanyDisplayName = (companyInfo, fallback = "Digital Health Network") =>
  companyInfo?.commercialName || companyInfo?.businessName || fallback;

const isDataImage = (source) =>
  typeof source === "string" && source.trim().startsWith("data:image/");

export const getCompanyReportLogoSource = (companyInfo, fallback = noLogo) => {
  if (isDataImage(companyInfo?.logoBase64)) return companyInfo.logoBase64;
  if (isDataImage(fallback)) return fallback;
  return noLogo;
};

const splitLongAddressLine = (line, maxLength = 48) => {
  if (!line || line.length <= maxLength) return [line].filter(Boolean);

  const parts = [];
  let current = "";

  line.split(" ").forEach((word) => {
    const next = current ? `${current} ${word}` : word;
    if (next.length > maxLength && current) {
      parts.push(current);
      current = word;
    } else {
      current = next;
    }
  });

  if (current) parts.push(current);
  return parts;
};

export const getCompanyAddressLines = (address = "") => {
  const normalized = `${address || ""}`
    .replace(/\s+/g, " ")
    .replace(/\s*,\s*/g, ", ")
    .trim();

  if (!normalized) return [];

  const formatted = normalized
    .replace(/\s+(Edificio\b)/i, "\n$1")
    .replace(/\s+(Evaristo\s+Morales\b)/i, "\n$1")
    .replace(/\s+(Distrito\s+Nacional\b)/i, ", $1");

  return formatted
    .split("\n")
    .flatMap((line) => splitLongAddressLine(line.trim()))
    .filter(Boolean);
};

export const getCompanyReportInfo = (companyInfo, fallback = {}) => ({
  logoDataUrl: getCompanyReportLogoSource(companyInfo, fallback.logoDataUrl),
  name: getCompanyDisplayName(companyInfo, fallback.name || fallback.consultoryName),
  address: companyInfo?.address || fallback.address || fallback.consultoryAddress,
  addressLines: getCompanyAddressLines(
    companyInfo?.address || fallback.address || fallback.consultoryAddress,
  ),
  formattedAddress: getCompanyAddressLines(
    companyInfo?.address || fallback.address || fallback.consultoryAddress,
  ).join("\n"),
  rnc: companyInfo?.rnc || fallback.rnc || fallback.consultoryRnc,
  electronicTaxReceipt:
    companyInfo?.electronicTaxReceipt ?? fallback.electronicTaxReceipt,
});
